/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * FastAPI
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { mutationOptionsWithInteraction } from '../../mutationOptions'
import { customInstance } from '../../mutator'
import type { DefaultMemberCrossSectionsDTO, HTTPValidationError } from '../fastAPI.schemas'

/**
 * The organisation's starting cross sections for purlins, beams and columns.

Any member may read them. A user without an organisation gets the usual
403 ``user_not_assigned_to_organisation``.
 * @summary Get Organisation Default Member Cross Sections
 */
export const getOrganisationDefaultMemberCrossSections = (signal?: AbortSignal) => {
  return customInstance<DefaultMemberCrossSectionsDTO>({
    url: `/my-organisation/cross-sections`,
    method: 'GET',
    signal,
  })
}

export const getGetOrganisationDefaultMemberCrossSectionsQueryKey = () => {
  return [`/my-organisation/cross-sections`] as const
}

export const getGetOrganisationDefaultMemberCrossSectionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>,
    TError,
    TData
  >
}) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = queryOptions?.queryKey ?? getGetOrganisationDefaultMemberCrossSectionsQueryKey()

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>
  > = ({ signal }) => getOrganisationDefaultMemberCrossSections(signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetOrganisationDefaultMemberCrossSectionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>
>
export type GetOrganisationDefaultMemberCrossSectionsQueryError = HTTPValidationError

/**
 * @summary Get Organisation Default Member Cross Sections
 */

export function useGetOrganisationDefaultMemberCrossSections<
  TData = Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationDefaultMemberCrossSections>>,
    TError,
    TData
  >
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOrganisationDefaultMemberCrossSectionsQueryOptions(options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * Replace the organisation's three default cross sections.

Only an administrator of the organisation may change them. Projects that
already exist keep the snapshots they copied at creation.
 * @summary Update Organisation Default Member Cross Sections
 */
export const updateOrganisationDefaultMemberCrossSections = (
  defaultMemberCrossSectionsDTO: DefaultMemberCrossSectionsDTO,
) => {
  return customInstance<DefaultMemberCrossSectionsDTO>({
    url: `/my-organisation/cross-sections`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: defaultMemberCrossSectionsDTO,
  })
}

export const useUpdateOrganisationDefaultMemberCrossSectionsMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganisationDefaultMemberCrossSections>>,
    TError,
    { data: DefaultMemberCrossSectionsDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOrganisationDefaultMemberCrossSections>>,
  TError,
  { data: DefaultMemberCrossSectionsDTO },
  TContext
> => {
  const mutationKey = ['updateOrganisationDefaultMemberCrossSections']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOrganisationDefaultMemberCrossSections>>,
    { data: DefaultMemberCrossSectionsDTO }
  > = props => {
    const { data } = props ?? {}

    return updateOrganisationDefaultMemberCrossSections(data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/my-organisation/cross-sections` },
    {
      operationId:
        'update_organisation_default_member_cross_sections_my_organisation_cross_sections_put',
      operationName: 'updateOrganisationDefaultMemberCrossSections',
    },
  )

  return customOptions
}

export type UpdateOrganisationDefaultMemberCrossSectionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOrganisationDefaultMemberCrossSections>>
>
export type UpdateOrganisationDefaultMemberCrossSectionsMutationBody = DefaultMemberCrossSectionsDTO
export type UpdateOrganisationDefaultMemberCrossSectionsMutationError = HTTPValidationError

/**
 * @summary Update Organisation Default Member Cross Sections
 */
export const useUpdateOrganisationDefaultMemberCrossSections = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganisationDefaultMemberCrossSections>>,
    TError,
    { data: DefaultMemberCrossSectionsDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof updateOrganisationDefaultMemberCrossSections>>,
  TError,
  { data: DefaultMemberCrossSectionsDTO },
  TContext
> => {
  const mutationOptions = useUpdateOrganisationDefaultMemberCrossSectionsMutationOptions(options)

  return useMutation(mutationOptions)
}
/**
 * The cross sections stamped onto purlins, beams and columns created here.
 * @summary Get Project Default Member Cross Sections
 */
export const getProjectDefaultMemberCrossSections = (projectGuid: string, signal?: AbortSignal) => {
  return customInstance<DefaultMemberCrossSectionsDTO>({
    url: `/projects/${projectGuid}/cross-sections`,
    method: 'GET',
    signal,
  })
}

export const getGetProjectDefaultMemberCrossSectionsQueryKey = (projectGuid?: string) => {
  return [`/projects/${projectGuid}/cross-sections`] as const
}

export const getGetProjectDefaultMemberCrossSectionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>,
  TError = HTTPValidationError,
>(
  projectGuid: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>,
      TError,
      TData
    >
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ?? getGetProjectDefaultMemberCrossSectionsQueryKey(projectGuid)

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>
  > = ({ signal }) => getProjectDefaultMemberCrossSections(projectGuid, signal)

  return { queryKey, queryFn, enabled: !!projectGuid, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>,
    TError,
    TData
  > & { queryKey: QueryKey }
}

export type GetProjectDefaultMemberCrossSectionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>
>
export type GetProjectDefaultMemberCrossSectionsQueryError = HTTPValidationError

/**
 * @summary Get Project Default Member Cross Sections
 */

export function useGetProjectDefaultMemberCrossSections<
  TData = Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>,
  TError = HTTPValidationError,
>(
  projectGuid: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getProjectDefaultMemberCrossSections>>,
      TError,
      TData
    >
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetProjectDefaultMemberCrossSectionsQueryOptions(projectGuid, options)

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey }

  query.queryKey = queryOptions.queryKey

  return query
}

/**
 * Replace this project's three default cross sections.

Does not change members that already exist. Does not change the
organisation library.
 * @summary Update Project Default Member Cross Sections
 */
export const updateProjectDefaultMemberCrossSections = (
  projectGuid: string,
  defaultMemberCrossSectionsDTO: DefaultMemberCrossSectionsDTO,
) => {
  return customInstance<DefaultMemberCrossSectionsDTO>({
    url: `/projects/${projectGuid}/cross-sections`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: defaultMemberCrossSectionsDTO,
  })
}

export const useUpdateProjectDefaultMemberCrossSectionsMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProjectDefaultMemberCrossSections>>,
    TError,
    { projectGuid: string; data: DefaultMemberCrossSectionsDTO },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateProjectDefaultMemberCrossSections>>,
  TError,
  { projectGuid: string; data: DefaultMemberCrossSectionsDTO },
  TContext
> => {
  const mutationKey = ['updateProjectDefaultMemberCrossSections']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateProjectDefaultMemberCrossSections>>,
    { projectGuid: string; data: DefaultMemberCrossSectionsDTO }
  > = props => {
    const { projectGuid, data } = props ?? {}

    return updateProjectDefaultMemberCrossSections(projectGuid, data)
  }

  const customOptions = mutationOptionsWithInteraction(
    { ...mutationOptions, mutationFn },
    { url: `/projects/{projectGuid}/cross-sections` },
    {
      operationId:
        'update_project_default_member_cross_sections_projects__project_guid__cross_sections_put',
      operationName: 'updateProjectDefaultMemberCrossSections',
    },
  )

  return customOptions
}

export type UpdateProjectDefaultMemberCrossSectionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateProjectDefaultMemberCrossSections>>
>
export type UpdateProjectDefaultMemberCrossSectionsMutationBody = DefaultMemberCrossSectionsDTO
export type UpdateProjectDefaultMemberCrossSectionsMutationError = HTTPValidationError

/**
 * @summary Update Project Default Member Cross Sections
 */
export const useUpdateProjectDefaultMemberCrossSections = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateProjectDefaultMemberCrossSections>>,
    TError,
    { projectGuid: string; data: DefaultMemberCrossSectionsDTO },
    TContext
  >
}): UseMutationResult<
  Awaited<ReturnType<typeof updateProjectDefaultMemberCrossSections>>,
  TError,
  { projectGuid: string; data: DefaultMemberCrossSectionsDTO },
  TContext
> => {
  const mutationOptions = useUpdateProjectDefaultMemberCrossSectionsMutationOptions(options)

  return useMutation(mutationOptions)
}
